import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { DocumentsWorkspace } from '../../components/shared/DocumentsWorkspace';
import { bookingsApi } from '../../services/api';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  FileText, 
  Upload,
  Download,
  Trash2,
  Loader2,
  File,
  FileCheck,
  Plus,
  Search,
  Package
} from 'lucide-react';
import { formatDate, attachmentFilename } from '../../lib/utils';
import { toast } from 'sonner';

const DOCUMENT_TYPES = [
  { value: 'bill_of_lading', label: 'Bill of Lading' },
  { value: 'commercial_invoice', label: 'Commercial Invoice' },
  { value: 'packing_list', label: 'Packing List' },
  { value: 'certificate_of_origin', label: 'Certificate of Origin' },
  { value: 'insurance_certificate', label: 'Insurance Certificate' },
  { value: 'customs_declaration', label: 'Customs Declaration' },
  { value: 'delivery_order', label: 'Delivery Order' },
  { value: 'cargo_manifest', label: 'Cargo Manifest' },
  { value: 'other', label: 'Other Document' },
];

function AgentDocuments() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const basePath = '/' + location.pathname.split('/')[1];
  const preselectedBooking = searchParams.get('booking') || '';
  
  const [bookings, setBookings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(preselectedBooking);
  const [documentType, setDocumentType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef(null);

  useEffect(function() {
    loadData();
  }, []);

  useEffect(function() {
    if (preselectedBooking) {
      setSelectedBooking(preselectedBooking);
    }
  }, [preselectedBooking]);

  async function loadData() {
    try {
      const [bookingsRes, docsRes] = await Promise.all([
        bookingsApi.getAll(),
        api.get('/documents')
      ]);
      setBookings(bookingsRes.data);
      setDocuments(docsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (!selectedBooking) {
      toast.error('Please select a booking first');
      return;
    }

    if (!documentType) {
      toast.error('Please select document type');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);

      await api.post('/bookings/' + selectedBooking + '/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document uploaded successfully');
      loadData();
      setDocumentType('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  }

  async function handleDownload(bookingId, documentId, filename) {
    try {
      const response = await api.get('/bookings/' + bookingId + '/documents/' + documentId, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download document');
    }
  }

  async function handleDelete(bookingId, documentId) {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.delete('/bookings/' + bookingId + '/documents/' + documentId);
      toast.success('Document deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete document');
    }
  }

  function getDocumentTypeLabel(type) {
    const docType = DOCUMENT_TYPES.find(function(t) { return t.value === type; });
    return docType ? docType.label : type;
  }

  const filteredDocuments = documents.filter(function(doc) {
    if (!searchTerm) return true;
    return doc.booking_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
           doc.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           doc.document_type?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="space-y-6" data-testid="agent-documents">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-[32px] sv-display text-ink">Documents</h1>
          <p className="text-gray-600 mt-1">Upload, manage, and download shipping documents</p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-sm shadow-card p-6">
        <h2 className="text-lg font-medium text-ink mb-4 flex items-center gap-2">
          <Upload className="h-5 w-5 text-blue-600" />
          Upload Document
        </h2>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="font-medium text-gray-700">Select booking</Label>
            <Select value={selectedBooking} onValueChange={setSelectedBooking}>
              <SelectTrigger className="h-12 rounded-sm">
                <SelectValue placeholder="Choose booking" />
              </SelectTrigger>
              <SelectContent>
                {bookings.map(function(booking) {
                  return (
                    <SelectItem key={booking.id} value={booking.id}>
                      {booking.booking_number} - {booking.origin_port.split(',')[0]} → {booking.destination_port.split(',')[0]}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="font-medium text-gray-700">Document type</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger className="h-12 rounded-sm">
                <SelectValue placeholder="Choose type" />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map(function(type) {
                  return (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="font-medium text-gray-700">File</Label>
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileUpload}
                className="hidden"
                id="agent-file-upload"
              />
              <label
                htmlFor="agent-file-upload"
                className={"flex items-center justify-center h-12 px-4 rounded-sm border-2 border-dashed cursor-pointer transition-colors " + (
                  uploading ? 'border-gray-200 bg-gray-50' : 'border-blue-500 hover:bg-blue-500/5'
                )}
              >
                {uploading ? (
                  <><Loader2 className="h-5 w-5 animate-spin text-gray-400 mr-2" /> Uploading...</>
                ) : (
                  <><Plus className="h-5 w-5 text-blue-600 mr-2" /> Choose File</>
                )}
              </label>
            </div>
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-3">
          Accepted formats: PDF, JPEG, PNG, DOC, DOCX (max 10MB)
        </p>
      </div>

      <DocumentsWorkspace bookings={bookings} basePath={basePath} initialBookingId={location.state?.bookingId} />

      {/* Documents List */}
      <div className="bg-white rounded-sm shadow-card overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-medium text-ink flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            All Documents ({filteredDocuments.length})
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filteredDocuments.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {filteredDocuments.map(function(doc) {
              return (
                <div key={doc.id} className="p-4 flex items-center justify-between hover:bg-gray-50" data-testid="document-row">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-ink">{doc.filename || doc.name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="sv-mono bg-gray-100 px-2 py-0.5 rounded">{doc.booking_number}</span>
                        <span>•</span>
                        <Badge variant="outline" className="text-xs">{getDocumentTypeLabel(doc.document_type || doc.type)}</Badge>
                        <span>•</span>
                        <span>{formatDate(doc.uploaded_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-full"
                      onClick={function() { handleDownload(doc.booking_id, doc.id, doc.filename || doc.name); }}
                      data-testid="document-download-btn"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-full text-red-500 hover:bg-red-50"
                      onClick={function() { handleDelete(doc.booking_id, doc.id); }}
                      data-testid="document-delete-btn"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No documents found</p>
            <p className="text-sm text-gray-400 mt-1">Upload documents using the form above</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentDocuments;
